import { ENV } from '@forwardflow/helpers/configs'

/**
 * THE MCP LOGIN CONTINUATION.
 *
 * When someone connects the FF MCP server to an assistant, the MCP
 * Worker sends them to Internal BE, which needs an authenticated session. If
 * they have none, Internal BE bounces them here with an opaque handle — and
 * without this module the handle is dropped and the connection can never
 * complete for a first-time or expired session.
 *
 * ONE PLACE owns validation, storage and the URL. Spreading those across the
 * password form, the SSO return and the navigation hook is how a
 * "continuation" quietly becomes an open redirect in one of the three.
 */

/** `?mcp=<handle>` — the only parameter this feature reads. */
export const MCP_PARAM = 'mcp'

/** Survives the round trip through Google or Microsoft. */
const STORAGE_KEY = 'mcp_continuation'

/**
 * base64url, 128 characters at most.
 *
 * A HANDLE, NEVER A URL. Accepting a URL here — even one that looks like ours —
 * would make this parameter a redirect target anyone could aim: open
 * `/signin?mcp=https://evil.example` and the dashboard would deliver a freshly
 * authenticated user to it. The pattern excludes `:` and `/`, so no URL can
 * satisfy it, and the destination is built from configuration below rather than
 * from anything the caller supplied.
 */
const HANDLE_PATTERN = /^[A-Za-z0-9_-]{1,128}$/

export const isValidMcpHandle = (value: unknown): value is string =>
  typeof value === 'string' && HANDLE_PATTERN.test(value)

/**
 * The handle from a query string, or null.
 *
 * A malformed value is treated as absent rather than as an error: the user is
 * trying to log in, and the ordinary flow is the right fallback.
 */
export const readMcpHandle = (search: string | URLSearchParams): string | null => {
  const raw = rawMcpParam(search)
  return isValidMcpHandle(raw) ? raw : null
}

/**
 * The parameter exactly as written, valid or not — `null` only when ABSENT.
 *
 * The distinction matters at one call site and nowhere else, but it matters
 * there: `?mcp=` present and malformed is a statement about which continuation
 * this login is for, and the answer to a bad one is "none", not "whichever one
 * happened to be left in storage".
 */
const rawMcpParam = (search: string | URLSearchParams): string | null => {
  const params =
    typeof search === 'string' ? new URLSearchParams(search) : search
  return params.get(MCP_PARAM)
}

/**
 * Remember it across an SSO round trip.
 *
 * `sessionStorage`, not `localStorage`: this is meaningful for one login in one
 * tab, and a value left in `localStorage` would silently redirect a completely
 * unrelated login days later.
 */
export const rememberMcpHandle = (handle: string): void => {
  if (!isValidMcpHandle(handle)) return
  try {
    sessionStorage.setItem(STORAGE_KEY, handle)
  } catch {
    // Private mode, or storage disabled. The password path still works from the
    // query string; only the SSO round trip loses the continuation, and losing
    // it means an ordinary login rather than a broken one.
  }
}

/** Read it back, re-validating — stored values are still untrusted input. */
export const recallMcpHandle = (): string | null => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY)
    return isValidMcpHandle(stored) ? stored : null
  } catch {
    return null
  }
}

/** Forget it. Called after use AND after failure — see `consumeMcpHandle`. */
export const clearMcpHandle = (): void => {
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    /* nothing to clear */
  }
}

/**
 * The handle for this login, from the URL or from an SSO round trip.
 *
 * CONSUMED: cleared as it is read, so a handle cannot leak into the next login
 * in the same tab. Internal BE's `/resume` is itself single-use and does not
 * consume on an unauthenticated visit, so a genuine retry re-enters through
 * `/authorize` rather than replaying this one.
 */
export const consumeMcpHandle = (
  search?: string | URLSearchParams,
): string | null => {
  // AN EXPLICIT PARAMETER IS THE ANSWER, even when it is a bad one.
  //
  // Treating a malformed `?mcp=` as absent used to fall through to whatever
  // was in `sessionStorage`: arrive at `/signin?mcp=https://evil.example` with
  // a valid handle A still stored, and the login resumed A. Not an open
  // redirect — A is ours and the URL never becomes a destination — but it
  // resumes a continuation the request did not ask for, and it contradicts the
  // one thing this parameter promises about invalid input.
  //
  // So a present parameter is decided on its own merits, and stored state is
  // consulted only when there is no parameter at all, which is the SSO return.
  if (search !== undefined) {
    const raw = rawMcpParam(search)
    if (raw !== null) {
      clearMcpHandle()
      return isValidMcpHandle(raw) ? raw : null
    }
  }
  const handle = recallMcpHandle()
  clearMcpHandle()
  return handle
}

/**
 * Where to send an authenticated browser.
 *
 * BUILT HERE, from the configured API origin and a fixed path. Nothing from the
 * query string reaches it except the handle, which the pattern above has
 * already restricted to characters that cannot form a URL.
 */
export const mcpResumeUrl = (handle: string): string | null => {
  if (!isValidMcpHandle(handle)) return null
  const base = String(ENV.PUBLIC_FF_URL ?? '').replace(/\/+$/, '')
  if (!base) return null
  return `${base}/auth/mcp/resume?c=${encodeURIComponent(handle)}`
}

/**
 * Leave the SPA for the MCP handoff, if this login was one.
 *
 * Returns whether it navigated, so the caller can skip its ordinary routing —
 * the continuation must win over dashboard and onboarding navigation, or the
 * user lands on their dashboard having apparently connected nothing.
 */
export const resumeMcpIfPending = (
  search?: string | URLSearchParams,
): boolean => {
  const handle = consumeMcpHandle(search)
  if (!handle) return false
  const url = mcpResumeUrl(handle)
  if (!url) return false
  // A full navigation, not a router push: the destination is another origin.
  window.location.href = url
  return true
}
